// Stage 7 · Milestone 2 — Client-side AES-256-GCM recovery locker.
// Uses ONLY the browser-native Web Crypto API (window.crypto.subtle): zero
// external dependencies, zero server compute, $0.00 footprint. Seed phrases,
// private keys and sign-in origin maps are encrypted locally BEFORE they ever
// touch the network — the backend only ever stores an unreadable cipher bundle.

const subtle = () => window.crypto.subtle;
const textEnc = new TextEncoder();
const textDec = new TextDecoder();

const b64 = (buf) => btoa(String.fromCharCode(...new Uint8Array(buf)));
const unb64 = (str) => Uint8Array.from(atob(str), (c) => c.charCodeAt(0));

const PBKDF2_ITERS = 210000;

export function cryptoAvailable() {
  return !!(window.crypto && window.crypto.subtle && window.crypto.getRandomValues);
}

async function deriveKek(passphrase, saltBytes) {
  const base = await subtle().importKey(
    "raw", textEnc.encode(passphrase), "PBKDF2", false, ["deriveKey"]);
  return subtle().deriveKey(
    { name: "PBKDF2", salt: saltBytes, iterations: PBKDF2_ITERS, hash: "SHA-256" },
    base, { name: "AES-GCM", length: 256 }, false, ["encrypt", "decrypt"]);
}

async function aesEncrypt(key, plaintext) {
  const iv = window.crypto.getRandomValues(new Uint8Array(12));
  const ct = await subtle().encrypt({ name: "AES-GCM", iv }, key, textEnc.encode(plaintext));
  return { iv: b64(iv), ct: b64(ct) };
}

async function aesDecrypt(key, ivB64, ctB64) {
  const pt = await subtle().decrypt({ name: "AES-GCM", iv: unb64(ivB64) }, key, unb64(ctB64));
  return textDec.decode(pt);
}

// Wrap a raw 256-bit DEK under a passphrase-derived key (KEK).
async function wrapDek(dekBytes, passphrase) {
  const salt = window.crypto.getRandomValues(new Uint8Array(16));
  const kek = await deriveKek(passphrase, salt);
  const iv = window.crypto.getRandomValues(new Uint8Array(12));
  const ct = await subtle().encrypt({ name: "AES-GCM", iv }, kek, dekBytes);
  return { salt: b64(salt), iv: b64(iv), ct: b64(ct) };
}

async function unwrapDek(wrap, passphrase) {
  const kek = await deriveKek(passphrase, unb64(wrap.salt));
  const dek = await subtle().decrypt(
    { name: "AES-GCM", iv: unb64(wrap.iv) }, kek, unb64(wrap.ct));
  return new Uint8Array(dek);
}

// Seal a secrets object into an opaque cipher bundle. A random DEK encrypts the
// payload; the DEK is then wrapped under the app PIN and (optionally) a
// biometric-derived passphrase so EITHER factor can later unlock it.
export async function sealLocker(secrets, { pin, bioPass }) {
  if (!cryptoAvailable()) throw new Error("Secure crypto unavailable on this device");
  const dek = window.crypto.getRandomValues(new Uint8Array(32));
  const dekKey = await subtle().importKey("raw", dek, { name: "AES-GCM" }, false, ["encrypt", "decrypt"]);
  const secret = await aesEncrypt(dekKey, JSON.stringify(secrets));
  const bundle = {
    v: 1,
    secret,
    wrap_pin: await wrapDek(dek, pin),
    wrap_bio: bioPass ? await wrapDek(dek, bioPass) : null,
    meta: {
      has_seed: !!(secrets.seed_phrase && secrets.seed_phrase.trim()),
      has_pk: !!(secrets.private_key && secrets.private_key.trim()),
      has_origin: !!(secrets.origin_type || (secrets.origin_detail && secrets.origin_detail.trim())),
      bio: !!bioPass,
    },
  };
  dek.fill(0); // scrub the raw DEK from memory
  return bundle;
}

// Open an opaque cipher bundle with either the PIN or the biometric passphrase.
export async function openLocker(bundle, { pin, bioPass }) {
  if (!cryptoAvailable()) throw new Error("Secure crypto unavailable on this device");
  const usePin = !!pin;
  const wrap = usePin ? bundle.wrap_pin : bundle.wrap_bio;
  if (!wrap) throw new Error("This unlock method is not available for this locker");
  const dek = await unwrapDek(wrap, usePin ? pin : bioPass); // throws on wrong key (GCM tag)
  const dekKey = await subtle().importKey("raw", dek, { name: "AES-GCM" }, false, ["decrypt"]);
  const json = await aesDecrypt(dekKey, bundle.secret.iv, bundle.secret.ct);
  dek.fill(0);
  return JSON.parse(json);
}

// Direct PIN-keyed JSON encryption (Sub-Coin Tax Ledger). The AES-256 key is
// derived straight from the step-up PIN — DB stores only { v, salt, iv, ct }.
export async function encryptJSON(obj, passphrase) {
  if (!cryptoAvailable()) throw new Error("Secure crypto unavailable on this device");
  const salt = window.crypto.getRandomValues(new Uint8Array(16));
  const key = await deriveKek(passphrase, salt);
  const { iv, ct } = await aesEncrypt(key, JSON.stringify(obj));
  return { v: 1, salt: b64(salt), iv, ct };
}

export async function decryptJSON(bundle, passphrase) {
  if (!cryptoAvailable()) throw new Error("Secure crypto unavailable on this device");
  const key = await deriveKek(passphrase, unb64(bundle.salt));
  const json = await aesDecrypt(key, bundle.iv, bundle.ct); // throws on wrong PIN
  return JSON.parse(json);
}
